import { supabase } from '@/config/supabase'
import { authService } from '@/services/auth'
import { Room, Player, GameRound, Prompt, Vote, RoomSettings, UserWord, ContentReport, ContentReportReason } from '@/types'

export class DatabaseService {
  // Generate unique 6-digit room code with RPC fallback
  private async generateRoomCode(): Promise<string> {
    // Try RPC first
    try {
      const { data, error } = await supabase.rpc('generate_room_code')
      if (!error && typeof data === 'string' && data.length === 6) {
        return data
      }
    } catch (e) {
      // ignore and fallback
    }

    // Client-side generation with best-effort collision check
    for (let i = 0; i < 10; i++) {
      const candidate = Math.floor(100000 + Math.random() * 900000).toString()
      try {
        const { data: rows } = await supabase
          .from('rooms')
          .select('id')
          .eq('code', candidate)
          .limit(1)

        if (!rows || rows.length === 0) return candidate
      } catch (e) {
        // If uniqueness check fails, still return candidate to avoid blocking
        return candidate
      }
    }
    return Math.floor(100000 + Math.random() * 900000).toString()
  }

  private async cleanupExistingLobbyForHost(hostId: string): Promise<void> {
    try {
      // Instead of deleting old rooms, mark them as finished
      const { data: existingRooms, error: roomsReadError } = await supabase
        .from('rooms')
        .select('id, status')
        .eq('host_id', hostId)

      if (roomsReadError) throw roomsReadError

      if (existingRooms && existingRooms.length > 0) {
        const roomIds = existingRooms.map(r => r.id)
        // Try to mark finished via RPC for the first room; fallback to bulk update
        try {
          await supabase.rpc('mark_room_finished', { p_room_id: roomIds[0] })
        } catch (e) {
          await supabase
            .from('rooms')
            .update({ status: 'finished' })
            .in('id', roomIds)
        }
      }

      // Remove lingering player row tied to this user in any room (by user_id)
      await supabase
        .from('players')
        .delete()
        .eq('user_id', hostId)
    } catch (error) {
      console.error('Error cleaning up existing lobby for host:', error)
      // Do not block room creation on cleanup errors
    }
  }

  // Room operations
  async createRoom(
    name: string,
    hostName: string,
    settings: Partial<RoomSettings> = {},
    gameMode: 'word_to_prompt' | 'user_word_to_prompt' = 'word_to_prompt'
  ): Promise<{ room: Room; player: Player }> {
    try {
      // Ensure user is authenticated
      const authUser = await authService.ensureAuthenticated()

      // Ensure host has no stale lobby/player before creating a new one
      await this.cleanupExistingLobbyForHost(authUser.id)

      // Create host player first (use generated id and store user_id)
      const { data: playerData, error: playerError } = await supabase
        .from('players')
        .insert({
          user_id: authUser.id,
          name: hostName,
          is_host: true,
          is_ready: true,
        })
        .select()
        .single()

      if (playerError) throw playerError

      // Create room with host_id and generated 6-digit code
      const code = await this.generateRoomCode()
      const { data: roomData, error: roomError } = await supabase
        .from('rooms')
        .insert({
          code,
          name,
          host_id: authUser.id,
          game_mode: gameMode,
          max_players: settings.maxPlayers || 6,
          max_rounds: settings.maxRounds || 3,
          prompt_time_limit: settings.promptTimeLimit || 300,
          voting_time_limit: settings.votingTimeLimit || 180,
        })
        .select()
        .single()

      if (roomError) throw roomError

      // Update player with room_id
      await supabase
        .from('players')
        .update({ room_id: roomData.id })
        .eq('id', playerData.id)

      const room: Room = {
        id: roomData.id,
        code: roomData.code,
        name: roomData.name,
        hostId: authUser.id,
        players: [this.mapPlayerFromDB(playerData)],
        status: roomData.status,
        gameMode: roomData.game_mode || 'word_to_prompt',
        maxPlayers: roomData.max_players,
        roundNumber: roomData.round_number,
        maxRounds: roomData.max_rounds,
        createdAt: roomData.created_at,
        settings: {
          promptTimeLimit: roomData.prompt_time_limit,
          votingTimeLimit: roomData.voting_time_limit,
          maxPlayers: roomData.max_players,
          maxRounds: roomData.max_rounds,
        },
      }

      return {
        room,
        player: this.mapPlayerFromDB(playerData),
      }
    } catch (error) {
      console.error('Error creating room:', error)
      throw error
    }
  }

  async joinRoom(roomCode: string, playerName: string): Promise<{ room: Room; player: Player }> {
    try {
      // Ensure user is authenticated
      const authUser = await authService.ensureAuthenticated()

      // Check if room exists and has space
      const { data: roomData, error: roomError } = await supabase
        .from('rooms')
        .select('*, players(*)')
        .eq('code', roomCode)
        .maybeSingle()

      if (roomError) throw roomError
      if (!roomData) throw new Error('Oda bulunamadı')
      if (roomData.players.length >= roomData.max_players) {
        throw new Error('Oda dolu')
      }
      if (roomData.status !== 'waiting') {
        throw new Error('Oyun başlamış, katılamazsınız')
      }

      // Generate unique name with suffix if needed
      let uniqueName = playerName
      const existingNames = roomData.players.map((p: any) => p.name as string)

      if (existingNames.includes(playerName)) {
        // Find the next available suffix number
        let suffix = 2
        while (existingNames.includes(`${playerName} (${suffix})`)) {
          suffix++
        }
        uniqueName = `${playerName} (${suffix})`
      }

      // Check if user is already in room by user_id
      const userExists = roomData.players.some((p: any) => p.user_id === authUser.id)
      if (userExists) {
        throw new Error('Bu odaya zaten katıldınız')
      }

      // Create player (generated id, store user_id)
      const { data: playerData, error: playerError } = await supabase
        .from('players')
        .insert({
          user_id: authUser.id,
          room_id: roomData.id,
          name: uniqueName,
          is_host: false,
          is_ready: false,
        })
        .select()
        .single()

      if (playerError) throw playerError

      // Get updated room data
      const room = await this.getRoom(roomData.id)

      return {
        room,
        player: this.mapPlayerFromDB(playerData),
      }
    } catch (error) {
      console.error('Error joining room:', error)
      throw error
    }
  }

  async getRoom(roomId: string): Promise<Room> {
    try {
      const { data, error } = await supabase
        .from('rooms')
        .select(`
          *,
          players(*)
        `)
        .eq('id', roomId)
        .maybeSingle()

      if (error) throw error
      if (!data) throw new Error('Oda bulunamadı')

      return {
        id: data.id,
        code: data.code,
        name: data.name,
        hostId: data.host_id,
        players: data.players.map(this.mapPlayerFromDB),
        status: data.status,
        gameMode: data.game_mode || 'word_to_prompt',
        currentWord: data.current_word,
        currentWordTr: (data as any).current_word_tr,
        currentWordEn: (data as any).current_word_en,
        currentVotingPromptId: data.current_voting_prompt_id,
        roundStartedAt: data.round_started_at,
        votingStartedAt: data.voting_started_at,
        maxPlayers: data.max_players,
        roundNumber: data.round_number,
        maxRounds: data.max_rounds,
        createdAt: data.created_at,
        settings: {
          promptTimeLimit: data.prompt_time_limit,
          votingTimeLimit: data.voting_time_limit,
          maxPlayers: data.max_players,
          maxRounds: data.max_rounds,
        },
      }
    } catch (error) {
      console.error('Error getting room:', error)
      throw error
    }
  }

  async updateRoomSettings(roomId: string, hostId: string, settings: Partial<RoomSettings>, gameMode?: 'word_to_prompt' | 'user_word_to_prompt'): Promise<void> {
    try {
      // Verify host permission
      const { data: roomData, error: roomError } = await supabase
        .from('rooms')
        .select('host_id, status')
        .eq('id', roomId)
        .single()

      if (roomError) throw roomError
      if (!roomData) throw new Error('Oda bulunamadı')
      if (roomData.host_id !== hostId) {
        throw new Error('Sadece lobi sahibi ayarları değiştirebilir')
      }

      // Check if game has started (allow mode change when waiting, results, or finished)
      if (roomData.status !== 'waiting' && roomData.status !== 'results' && roomData.status !== 'finished' && gameMode) {
        throw new Error('Oyun başladıktan sonra oyun modu değiştirilemez')
      }

      const updateData: any = {}
      if (settings.maxPlayers !== undefined) updateData.max_players = settings.maxPlayers
      if (settings.maxRounds !== undefined) updateData.max_rounds = settings.maxRounds
      if (settings.promptTimeLimit !== undefined) updateData.prompt_time_limit = settings.promptTimeLimit
      if (settings.votingTimeLimit !== undefined) updateData.voting_time_limit = settings.votingTimeLimit
      if (gameMode !== undefined) updateData.game_mode = gameMode

      const { error } = await supabase
        .from('rooms')
        .update(updateData)
        .eq('id', roomId)

      if (error) throw error
    } catch (error) {
      console.error('Error updating room settings:', error)
      throw error
    }
  }

  async updatePlayerReady(playerId: string, isReady: boolean): Promise<void> {
    try {
      const { error } = await supabase
        .from('players')
        .update({ is_ready: isReady })
        .eq('id', playerId)

      if (error) throw error
    } catch (error) {
      console.error('Error updating player ready status:', error)
      throw error
    }
  }

  async leaveRoom(roomId: string, playerId: string): Promise<void> {
    try {
      // Check if leaving player is host
      const { data: playerRow, error: playerError } = await supabase
        .from('players')
        .select('id, is_host')
        .eq('id', playerId)
        .eq('room_id', roomId)
        .single()

      if (playerError && playerError.code !== 'PGRST116') {
        throw playerError
      }

      // If host is leaving, mark room finished instead of deleting
      if (playerRow?.is_host) {
        const { error: finishError } = await supabase
          .from('rooms')
          .update({ status: 'finished' })
          .eq('id', roomId)
        if (finishError) throw finishError
        // Remove host player row
        await supabase.from('players').delete().eq('id', playerId).eq('room_id', roomId)
        return
      }

      // Non-host: remove only the player
      const { error: deleteError } = await supabase
        .from('players')
        .delete()
        .eq('id', playerId)
        .eq('room_id', roomId)

      if (deleteError) throw deleteError

      // If room becomes empty, mark finished
      const { count, error: countError } = await supabase
        .from('players')
        .select('id', { count: 'exact', head: true })
        .eq('room_id', roomId)

      if (countError) throw countError

      if ((count || 0) === 0) {
        await supabase
          .from('rooms')
          .update({ status: 'finished' })
          .eq('id', roomId)
      }
    } catch (error) {
      console.error('Error leaving room (finish instead of delete):', error)
      throw error
    }
  }

  async unreadyAllNonHostPlayers(roomId: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('players')
        .update({ is_ready: false })
        .eq('room_id', roomId)
        .eq('is_host', false)

      if (error) throw error
    } catch (error) {
      console.error('Error unreadying non-host players:', error)
      throw error
    }
  }

  async startGame(roomId: string): Promise<string> {
    try {
      // Get room settings to determine max rounds
      const { data: roomData, error: roomReadError } = await supabase
        .from('rooms')
        .select('max_rounds')
        .eq('id', roomId)
        .single()

      if (roomReadError) throw roomReadError

      const maxRounds = roomData.max_rounds || 3

      // Create all rounds at once (bilingual aware)
      const roundsToInsert: any[] = []
      // const chosenRows: { id: string; word_tr: string | null; word_en: string | null }[] = []

      // detect UI lang from persistence
      let uiLang: string = 'tr'
      try {
        const stored = typeof localStorage !== 'undefined' ? localStorage.getItem('i18nextLng') : null
        if (stored && stored.length > 0) uiLang = stored.split('-')[0]
        else if (typeof navigator !== 'undefined' && navigator.language) uiLang = navigator.language.split('-')[0]
      } catch { }

      for (let round = 1; round <= maxRounds; round++) {
        // Prefer RPC to avoid PostgREST random() ordering issues
        let localized: string | null = null
        try {
          const { data: rpcWord, error: rpcError } = await supabase.rpc('get_random_word_by_lang', { p_lang: uiLang })
          if (!rpcError && typeof rpcWord === 'string' && rpcWord.length > 0) {
            localized = rpcWord
          }
        } catch { }

        if (!localized) {
          const fallback = await this.getRandomWord()
          roundsToInsert.push({
            room_id: roomId,
            round_number: round,
            word: fallback,
            status: 'prompt_writing',
          })
          continue
        }

        roundsToInsert.push({
          room_id: roomId,
          round_number: round,
          word: localized,
          status: 'prompt_writing',
        })
      }

      // Insert all rounds
      const { error: roundError } = await supabase
        .from('game_rounds')
        .upsert(roundsToInsert, { onConflict: 'room_id,round_number' })

      if (roundError) throw roundError

      // Update room status and set first word with server-side start time
      let firstWord = roundsToInsert[0]?.word
      let firstTr = (roundsToInsert[0] as any)?.word_tr || null
      let firstEn = (roundsToInsert[0] as any)?.word_en || null
      let firstId = (roundsToInsert[0] as any)?.word_id || null

      const { error: roomError } = await supabase
        .from('rooms')
        .update({
          status: 'prompt_writing',
          current_word: firstWord,
          current_word_tr: firstTr,
          current_word_en: firstEn,
          current_word_id: firstId,
          round_number: 1,
          round_started_at: new Date().toISOString() // Set server-side start time
        })
        .eq('id', roomId)

      if (roomError) throw roomError

      return firstWord
    } catch (error) {
      console.error('Error starting game:', error)
      throw error
    }
  }

  async advanceToVoting(roomId: string, roundNumber: number): Promise<void> {
    try {
      // Update room and round atomically best-effort
      const { error: roomError } = await supabase
        .from('rooms')
        .update({ status: 'voting' })
        .eq('id', roomId)
        .in('status', ['prompt_writing', 'generating'])

      if (roomError) throw roomError

      const { error: roundError } = await supabase
        .from('game_rounds')
        .update({ status: 'voting' })
        .eq('room_id', roomId)
        .eq('round_number', roundNumber)

      if (roundError) throw roundError
    } catch (error) {
      console.error('Error advancing to voting:', error)
      throw error
    }
  }

  async advanceToResults(roomId: string, roundNumber: number): Promise<void> {
    try {
      const { error: roomError } = await supabase
        .from('rooms')
        .update({ status: 'results' })
        .eq('id', roomId)
        .eq('status', 'voting')

      if (roomError) throw roomError

      const { error: roundError } = await supabase
        .from('game_rounds')
        .update({ status: 'completed', end_time: new Date().toISOString() })
        .eq('room_id', roomId)
        .eq('round_number', roundNumber)

      if (roundError) throw roundError
    } catch (error) {
      console.error('Error advancing to results:', error)
      throw error
    }
  }

  // Advance to next voting prompt manually (host or timer)
  async advanceToNextVotingPrompt(roomId: string, triggeredByPlayerId?: string): Promise<{ moved: boolean; finished: boolean }> {
    try {
      // Load room and players
      const { data: roomData, error: roomError } = await supabase
        .from('rooms')
        .select('id, host_id, status, voting_started_at')
        .eq('id', roomId)
        .single()

      if (roomError) throw roomError
      if (!roomData) throw new Error('Oda bulunamadı')
      if (roomData.status !== 'voting') return { moved: false, finished: false }

      // If triggered by player, verify host permission
      if (triggeredByPlayerId) {
        const { data: player, error: playerError } = await supabase
          .from('players')
          .select('id, is_host')
          .eq('id', triggeredByPlayerId)
          .eq('room_id', roomId)
          .single()

        if (playerError) throw playerError
        if (!player || !player.is_host) {
          throw new Error('Sadece lobi sahibi ilerletebilir')
        }
      }

      // Compute next prompt that still needs votes
      // 1) Count players
      const { data: players, error: playersError } = await supabase
        .from('players')
        .select('id')
        .eq('room_id', roomId)

      if (playersError) throw playersError
      const totalPlayers = (players || []).length

      // 2) Get prompts eligible for voting
      const { data: prompts, error: promptsError } = await supabase
        .from('prompts')
        .select('id')
        .eq('room_id', roomId)
        .eq('image_generation_status', 'completed')
        .not('image_url', 'is', null)

      if (promptsError) throw promptsError
      const promptIds = (prompts || []).map(p => p.id)

      if (promptIds.length === 0) {
        // Nothing to vote, finish
        const { error: finishError } = await supabase
          .from('rooms')
          .update({ status: 'results' })
          .eq('id', roomId)
        if (finishError) throw finishError
        return { moved: false, finished: true }
      }

      // 3) Find first prompt with missing votes
      const { data: votesAgg, error: votesAggError } = await supabase
        .from('votes')
        .select('prompt_id, count:prompt_id', { count: 'exact' })
        .in('prompt_id', promptIds)

      if (votesAggError) throw votesAggError

      const countsByPrompt = new Map<string, number>()
        ; (votesAgg as any[] | null)?.forEach((row: any) => {
          countsByPrompt.set(row.prompt_id, row.count || 0)
        })

      // Fetch ordered prompts to decide the next one deterministically
      const { data: orderedPrompts, error: orderedError } = await supabase
        .from('prompts')
        .select('id, round_number, created_at')
        .eq('room_id', roomId)
        .eq('image_generation_status', 'completed')
        .not('image_url', 'is', null)
        .order('round_number', { ascending: true })
        .order('created_at', { ascending: true })

      if (orderedError) throw orderedError

      const target = (orderedPrompts || []).find(p => (countsByPrompt.get(p.id) || 0) < (totalPlayers - 1))

      if (!target) {
        // All prompts fully voted → move to results
        const { error: finishError } = await supabase
          .from('rooms')
          .update({ status: 'results' })
          .eq('id', roomId)
        if (finishError) throw finishError
        return { moved: false, finished: true }
      }

      // Update room current voting prompt and reset timer
      const { error: roomUpdateError } = await supabase
        .from('rooms')
        .update({ current_voting_prompt_id: target.id, voting_started_at: new Date().toISOString() })
        .eq('id', roomId)

      if (roomUpdateError) throw roomUpdateError

      return { moved: true, finished: false }
    } catch (error) {
      console.error('Error advancing to next voting prompt:', error)
      throw error
    }
  }

  // New: mark prompts collection complete across all rounds and switch to voting phase
  async finalizeAllPromptsAndStartVoting(roomId: string): Promise<void> {
    try {
      // Move room to voting over all rounds
      const { error: roomError } = await supabase
        .from('rooms')
        .update({ status: 'voting' })
        .eq('id', roomId)
        .in('status', ['prompt_writing', 'generating'])

      if (roomError) throw roomError

      // Mark all rounds as voting
      const { error: roundsError } = await supabase
        .from('game_rounds')
        .update({ status: 'voting' })
        .eq('room_id', roomId)

      if (roundsError) throw roundsError
    } catch (error) {
      console.error('Error finalizing all prompts and starting voting:', error)
      throw error
    }
  }

  // New: get prompts for all rounds (optionally filter by round)
  async getAllRoundPrompts(roomId: string): Promise<Prompt[]> {
    try {
      const { data, error } = await supabase
        .from('prompts')
        .select('*')
        .eq('room_id', roomId)

      if (error) throw error
      return (data || []).map(this.mapPromptFromDB)
    } catch (error) {
      console.error('Error getting all round prompts:', error)
      throw error
    }
  }

  // Get all rounds for a room
  async getAllRounds(roomId: string): Promise<GameRound[]> {
    try {
      const { data, error } = await supabase
        .from('game_rounds')
        .select('*')
        .eq('room_id', roomId)
        .order('round_number', { ascending: true })

      if (error) throw error
      return (data || []).map(this.mapRoundFromDB)
    } catch (error) {
      console.error('Error getting all rounds:', error)
      throw error
    }
  }

  // New: get prompts by specific round (preserve existing name too)
  async getRoundPrompts(roomId: string, roundNumber: number): Promise<Prompt[]> {
    try {
      const { data, error } = await supabase
        .from('prompts')
        .select('*')
        .eq('room_id', roomId)
        .eq('round_number', roundNumber)

      if (error) throw error
      return (data || []).map(this.mapPromptFromDB)
    } catch (error) {
      console.error('Error getting round prompts:', error)
      throw error
    }
  }

  // New: get votes grouped by prompt across all rounds
  async getAllRoundVotes(roomId: string): Promise<Vote[]> {
    try {
      const { data, error } = await supabase
        .from('votes')
        .select('*')
        .eq('room_id', roomId)

      if (error) throw error
      const votes = (data || []).map(this.mapVoteFromDB)
      console.log('getAllRoundVotes: Found votes for room', roomId, ':', votes.length, votes.map(v => ({ id: v.id, promptId: v.promptId, score: v.score })))
      return votes
    } catch (error) {
      console.error('Error getting all round votes:', error)
      throw error
    }
  }
  async startNextRound(roomId: string): Promise<{ roundNumber: number; word: string; isLastRound: boolean }> {
    try {
      // Read room to compute next round
      const { data: roomData, error: roomReadError } = await supabase
        .from('rooms')
        .select('*')
        .eq('id', roomId)
        .single()

      if (roomReadError) throw roomReadError

      const nextRound = (roomData.round_number || 0) + 1
      const maxRounds = roomData.max_rounds || 3

      if (nextRound > maxRounds) {
        throw new Error('Maksimum tur sayısına ulaşıldı')
      }

      // Get word for this round from pre-created rounds
      const { data: roundData, error: roundError } = await supabase
        .from('game_rounds')
        .select('word, word_tr, word_en, word_id')
        .eq('room_id', roomId)
        .eq('round_number', nextRound)
        .single()

      if (roundError) throw roundError

      // Update room with next round info (including bilingual fields)
      const { error: updateRoomError } = await supabase
        .from('rooms')
        .update({
          current_word: roundData.word,
          current_word_tr: (roundData as any).word_tr || null,
          current_word_en: (roundData as any).word_en || null,
          current_word_id: (roundData as any).word_id || null,
          round_number: nextRound,
        })
        .eq('id', roomId)

      if (updateRoomError) throw updateRoomError

      return {
        roundNumber: nextRound,
        word: (roundData as any).word_tr || (roundData as any).word_en || (roundData as any).word,
        isLastRound: nextRound === maxRounds
      }
    } catch (error) {
      console.error('Error starting next round:', error)
      throw error
    }
  }

  async advanceToNextVotingRound(roomId: string): Promise<{ hasMore: boolean; nextRound?: number }> {
    try {
      // Read current room
      const { data: roomData, error: roomReadError } = await supabase
        .from('rooms')
        .select('*')
        .eq('id', roomId)
        .single()

      if (roomReadError) throw roomReadError

      const currentRound = roomData.round_number || 1
      const maxRounds = roomData.max_rounds || 1

      if (currentRound >= maxRounds) {
        // No more rounds, move to results
        await this.advanceToResults(roomId, currentRound)
        return { hasMore: false }
      }

      const nextRound = currentRound + 1

      // Set next round to voting and update room round_number
      const { error: roundError } = await supabase
        .from('game_rounds')
        .update({ status: 'voting' })
        .eq('room_id', roomId)
        .eq('round_number', nextRound)

      if (roundError) throw roundError

      const { error: roomUpdateError } = await supabase
        .from('rooms')
        .update({ status: 'voting', round_number: nextRound })
        .eq('id', roomId)

      if (roomUpdateError) throw roomUpdateError

      return { hasMore: true, nextRound }
    } catch (error) {
      console.error('Error advancing to next voting round:', error)
      throw error
    }
  }

  async submitPrompt(playerId: string, roomId: string, roundNumber: number, promptText: string): Promise<Prompt> {
    try {
      // Get round ID
      const { data: roundData, error: roundError } = await supabase
        .from('game_rounds')
        .select('id')
        .eq('room_id', roomId)
        .eq('round_number', roundNumber)
        .single()

      if (roundError) throw roundError

      const { data, error } = await supabase
        .from('prompts')
        .insert({
          player_id: playerId,
          room_id: roomId,
          round_id: roundData.id,
          round_number: roundNumber,
          text: promptText,
          image_generation_status: 'pending',
        })
        .select()
        .single()

      if (error) {
        // Handle duplicate key constraint specifically
        if (error.code === '23505' && error.message.includes('prompts_player_id_round_id_key')) {
          throw new Error('Bu tur için zaten bir prompt gönderdiniz')
        }
        throw error
      }

      return this.mapPromptFromDB(data)
    } catch (error) {
      console.error('Error submitting prompt:', error)
      throw error
    }
  }

  async updatePromptForRetry(promptId: string, newText: string): Promise<Prompt> {
    try {
      const { data, error } = await supabase
        .from('prompts')
        .update({
          text: newText,
          image_url: '',
          image_generation_status: 'pending',
          generation_error: null,
          is_locked: false,
          locked_at: null,
        })
        .eq('id', promptId)
        .select()
        .single()

      if (error) throw error

      return this.mapPromptFromDB(data)
    } catch (error) {
      console.error('Error updating prompt for retry:', error)
      throw error
    }
  }

  async lockPrompt(promptId: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('prompts')
        .update({ is_locked: true, locked_at: new Date().toISOString() })
        .eq('id', promptId)
      if (error) throw error
    } catch (error) {
      console.error('Error locking prompt:', error)
      throw error
    }
  }

  async updatePromptImage(promptId: string, imageUrl: string, status: 'completed' | 'failed', error?: string): Promise<void> {
    try {
      console.log('Database: Updating prompt image:', { promptId, imageUrl, status, error })

      const updateData: any = {
        image_url: imageUrl,
        image_generation_status: status,
      }

      if (error) {
        updateData.generation_error = error
      }

      const { data, error: updateError } = await supabase
        .from('prompts')
        .update(updateData)
        .eq('id', promptId)
        .select()

      if (updateError) throw updateError

      console.log('Database: Prompt image updated successfully:', data)
    } catch (error) {
      console.error('Error updating prompt image:', error)
      throw error
    }
  }

  async submitVote(voterId: string, promptId: string, roomId: string, roundNumber: number, score: number): Promise<void> {
    try {
      const { error } = await supabase
        .from('votes')
        .upsert({
          voter_id: voterId,
          prompt_id: promptId,
          room_id: roomId,
          round_number: roundNumber,
          score: score,
        }, { onConflict: 'voter_id,prompt_id', ignoreDuplicates: false })

      if (error) throw error
    } catch (error) {
      console.error('Error submitting vote:', error)
      throw error
    }
  }

  async getRandomWord(): Promise<string> {
    try {
      // Prefer language-aware function; read from i18n persistence if available
      let lang = 'tr'
      try {
        const stored = typeof localStorage !== 'undefined' ? localStorage.getItem('i18nextLng') : null
        if (stored && stored.length > 0) lang = stored.split('-')[0]
        else if (typeof navigator !== 'undefined' && navigator.language) lang = navigator.language.split('-')[0]
      } catch { }
      let { data, error } = await supabase.rpc('get_random_word_by_lang', { p_lang: lang })

      if (error || !data) {
        // Fallback: get all words and pick random one
        const { data: words, error: wordsError } = await supabase
          .from('word_bank')
          .select('word')

        if (wordsError || !words || words.length === 0) throw new Error('No words found')

        const randomIndex = Math.floor(Math.random() * words.length)
        return words[randomIndex].word
      }

      return data
    } catch (error) {
      console.error('Error getting random word:', error)
      // Fallback words if database fails
      let uiLang = 'tr'
      try {
        const stored = typeof localStorage !== 'undefined' ? localStorage.getItem('i18nextLng') : null
        if (stored && stored.length > 0) uiLang = stored.split('-')[0]
        else if (typeof navigator !== 'undefined' && navigator.language) uiLang = navigator.language.split('-')[0]
      } catch { }
      const fallbackTr = ['gizem', 'özgürlük', 'umut', 'cesaret', 'hayal', 'güç', 'zaman', 'aşk']
      const fallbackEn = ['mystery', 'freedom', 'hope', 'courage', 'dream', 'power', 'time', 'love']
      const pool = uiLang === 'en' ? fallbackEn : fallbackTr
      return pool[Math.floor(Math.random() * pool.length)]
    }
  }



  async hasExistingGameData(roomId: string): Promise<{ hasPrompts: boolean; hasRounds: boolean; hasUserWords: boolean }> {
    try {
      // Check for existing prompts
      const { data: promptsData, error: promptsError } = await supabase
        .from('prompts')
        .select('id')
        .eq('room_id', roomId)
        .limit(1)

      // Check for existing game rounds
      const { data: roundsData, error: roundsError } = await supabase
        .from('game_rounds')
        .select('id')
        .eq('room_id', roomId)
        .limit(1)

      // Check for existing user words
      let hasUserWords = false
      try {
        const { data: userWordsData, error: userWordsError } = await supabase
          .from('user_words')
          .select('id')
          .eq('room_id', roomId)
          .limit(1)
        hasUserWords = !userWordsError && userWordsData && userWordsData.length > 0
      } catch (e) {
        // Table may not exist in older schemas
        hasUserWords = false
      }

      return {
        hasPrompts: !promptsError && promptsData && promptsData.length > 0,
        hasRounds: !roundsError && roundsData && roundsData.length > 0,
        hasUserWords
      }
    } catch (error) {
      console.error('Error checking existing game data:', error)
      return { hasPrompts: false, hasRounds: false, hasUserWords: false }
    }
  }

  async resetRoomForRematch(roomId: string, triggeredByPlayerId: string): Promise<void> {
    try {
      // Verify that the triggered player is the host of this room
      const { data: roomData, error: roomError } = await supabase
        .from('rooms')
        .select('host_id')
        .eq('id', roomId)
        .single()

      if (roomError) throw roomError
      if (!roomData) throw new Error('Oda bulunamadı')

      const { data: kickerRow, error: kickerError } = await supabase
        .from('players')
        .select('user_id, room_id')
        .eq('id', triggeredByPlayerId)
        .single()

      if (kickerError || !kickerRow || kickerRow.room_id !== roomId) {
        throw new Error('Oda veya kullanıcı doğrulanamadı')
      }

      if (roomData.host_id !== kickerRow.user_id) {
        throw new Error('Sadece lobi sahibi yeni oyunu başlatabilir')
      }

      // 1) Clear foreign key references in rooms table first
      const { error: roomRefsError } = await supabase
        .from('rooms')
        .update({
          current_voting_prompt_id: null,
          current_word: null,
          current_word_tr: null,
          current_word_en: null,
          current_word_id: null
        })
        .eq('id', roomId)
      if (roomRefsError) throw roomRefsError

      // 2) Delete votes first (FK to prompts)
      console.log('resetRoomForRematch: Deleting votes for room:', roomId)

      // First check what votes exist before deletion
      const { data: existingVotes } = await supabase
        .from('votes')
        .select('id, prompt_id, voter_id, score')
        .eq('room_id', roomId)
      console.log('resetRoomForRematch: Found existing votes before deletion:', existingVotes?.length || 0, existingVotes)

      const { data: deletedVotes, error: votesDeleteError } = await supabase
        .from('votes')
        .delete()
        .eq('room_id', roomId)
        .select()
      if (votesDeleteError) throw votesDeleteError
      console.log('resetRoomForRematch: Deleted votes:', deletedVotes?.length || 0, deletedVotes)

      // Verify deletion worked
      const { data: remainingVotes } = await supabase
        .from('votes')
        .select('id, prompt_id, voter_id, score')
        .eq('room_id', roomId)
      console.log('resetRoomForRematch: Remaining votes after deletion:', remainingVotes?.length || 0, remainingVotes)

      // 3) Delete prompts
      const { error: promptsDeleteError } = await supabase
        .from('prompts')
        .delete()
        .eq('room_id', roomId)
      if (promptsDeleteError) throw promptsDeleteError

      // 4) Delete user words (for user_word_to_prompt mode)
      try {
        const { error: userWordsDeleteError } = await supabase
          .from('user_words')
          .delete()
          .eq('room_id', roomId)
        if (userWordsDeleteError) throw userWordsDeleteError
      } catch (e) {
        // table may not exist in older schemas; ignore
      }

      // 5) Delete game_rounds (this will ensure new round IDs are generated)
      const { error: roundsDeleteError } = await supabase
        .from('game_rounds')
        .delete()
        .eq('room_id', roomId)
      if (roundsDeleteError) throw roundsDeleteError

      // 6) Reset players ready state: keep host ready, unready others
      // Unready all non-host players
      const { error: unreadyNonHostError } = await supabase
        .from('players')
        .update({ is_ready: false })
        .eq('room_id', roomId)
        .eq('is_host', false)
      if (unreadyNonHostError) throw unreadyNonHostError

      // Ensure host stays ready
      const { error: hostReadyError } = await supabase
        .from('players')
        .update({ is_ready: true })
        .eq('room_id', roomId)
        .eq('is_host', true)
      if (hostReadyError) throw hostReadyError

      // 7) Reset room fields to initial waiting state
      const roomReset: any = {
        status: 'waiting',
        current_word: null,
        round_number: 0,
        round_started_at: null,
        voting_started_at: null,
        current_voting_prompt_id: null,
      }
        // Optional bilingual fields if exist
        ; (roomReset as any).current_word_tr = null
        ; (roomReset as any).current_word_en = null
        ; (roomReset as any).current_word_id = null

      const { error: roomUpdateError } = await supabase
        .from('rooms')
        .update(roomReset)
        .eq('id', roomId)
      if (roomUpdateError) throw roomUpdateError
    } catch (error) {
      console.error('Error resetting room for rematch:', error)
      throw error
    }
  }

  async finalizeRoundOnTimeout(roomId: string, roundNumber: number): Promise<void> {
    try {
      // Fetch all players
      const { data: players, error: playersError } = await supabase
        .from('players')
        .select('id')
        .eq('room_id', roomId)

      if (playersError) throw playersError

      // Fetch existing prompts for the round
      const { data: prompts, error: promptsError } = await supabase
        .from('prompts')
        .select('id, player_id')
        .eq('room_id', roomId)
        .eq('round_number', roundNumber)

      if (promptsError) throw promptsError

      const playerIds = (players || []).map(p => p.id)
      const promptedPlayerIds = new Set((prompts || []).map(p => p.player_id))

      // Insert placeholders for players without prompts
      const missingPlayerIds = playerIds.filter(id => !promptedPlayerIds.has(id))
      if (missingPlayerIds.length > 0) {
        // Need round id to insert
        const { data: roundData, error: roundError } = await supabase
          .from('game_rounds')
          .select('id')
          .eq('room_id', roomId)
          .eq('round_number', roundNumber)
          .single()

        if (roundError) throw roundError

        const insertPayload = missingPlayerIds.map(pid => ({
          player_id: pid,
          room_id: roomId,
          round_id: roundData.id,
          round_number: roundNumber,
          text: '',
          image_generation_status: 'failed',
          generation_error: 'time_expired',
          is_locked: true,
          locked_at: new Date().toISOString(),
        }))

        const { error: insertMissingError } = await supabase
          .from('prompts')
          .upsert(insertPayload, { onConflict: 'player_id,round_id', ignoreDuplicates: true })

        if (insertMissingError) throw insertMissingError
      }

      // Lock prompts that already have an image URL without changing their generation status
      const { error: lockWithUrlError } = await supabase
        .from('prompts')
        .update({
          is_locked: true,
          locked_at: new Date().toISOString(),
        })
        .eq('room_id', roomId)
        .eq('round_number', roundNumber)
        .is('is_locked', false)
        .not('image_url', 'is', null)
        .neq('image_url', '')

      if (lockWithUrlError) throw lockWithUrlError

      // Ensure any prompts that somehow have an image URL but status 'failed' are corrected to 'completed'
      const { error: fixFailedWithUrlError } = await supabase
        .from('prompts')
        .update({
          image_generation_status: 'completed',
          generation_error: null,
        })
        .eq('room_id', roomId)
        .eq('round_number', roundNumber)
        .eq('image_generation_status', 'failed')
        .not('image_url', 'is', null)
        .neq('image_url', '')

      if (fixFailedWithUrlError) throw fixFailedWithUrlError

      // Lock prompts without image URL and mark as failed due to timeout (image_url IS NULL)
      const { error: lockWithoutUrlNullError } = await supabase
        .from('prompts')
        .update({
          is_locked: true,
          locked_at: new Date().toISOString(),
          image_generation_status: 'failed',
          generation_error: 'time_expired'
        })
        .eq('room_id', roomId)
        .eq('round_number', roundNumber)
        .is('is_locked', false)
        .is('image_url', null)

      if (lockWithoutUrlNullError) throw lockWithoutUrlNullError

      // Lock prompts without image URL and mark as failed due to timeout (image_url = '')
      const { error: lockWithoutUrlEmptyError } = await supabase
        .from('prompts')
        .update({
          is_locked: true,
          locked_at: new Date().toISOString(),
          image_generation_status: 'failed',
          generation_error: 'time_expired'
        })
        .eq('room_id', roomId)
        .eq('round_number', roundNumber)
        .is('is_locked', false)
        .eq('image_url', '')

      if (lockWithoutUrlEmptyError) throw lockWithoutUrlEmptyError
    } catch (error) {
      console.error('Error finalizing round on timeout:', error)
      throw error
    }
  }

  async getRoundVotes(roomId: string, roundNumber: number): Promise<Vote[]> {
    try {
      const { data, error } = await supabase
        .from('votes')
        .select('*')
        .eq('room_id', roomId)
        .eq('round_number', roundNumber)

      if (error) throw error
      return data.map(this.mapVoteFromDB)
    } catch (error) {
      console.error('Error getting round votes:', error)
      throw error
    }
  }

  // Content reporting
  async reportContent(params: {
    roomId: string
    promptId: string
    reporterId: string
    imageUrl?: string
    reason: ContentReportReason
    details?: string
  }): Promise<ContentReport> {
    try {
      // Map auth user id to players.id in this room
      const { data: playerRow, error: playerErr } = await supabase
        .from('players')
        .select('id')
        .eq('room_id', params.roomId)
        .eq('user_id', params.reporterId)
        .single()

      if (playerErr || !playerRow) {
        throw new Error('Rapor göndermek için önce odaya katılmanız gerekir')
      }

      const { data, error } = await supabase
        .from('content_reports')
        .upsert({
          room_id: params.roomId,
          prompt_id: params.promptId,
          reporter_id: playerRow.id,
          image_url: params.imageUrl || null,
          reason: params.reason,
          details: params.details || null,
          status: 'pending',
        }, { onConflict: 'reporter_id,prompt_id', ignoreDuplicates: false })
        .select()
        .single()

      if (error) throw error

      return {
        id: data.id,
        roomId: data.room_id,
        promptId: data.prompt_id,
        reporterId: data.reporter_id,
        imageUrl: data.image_url || undefined,
        reason: data.reason,
        details: data.details || undefined,
        status: data.status,
        createdAt: data.created_at,
      }
    } catch (error) {
      console.error('Error reporting content:', error)
      throw error
    }
  }

  async hasReported(promptId: string, reporterId: string): Promise<boolean> {
    try {
      // We do not know the roomId here, so derive via prompt -> room_id
      const { data: promptRow, error: promptErr } = await supabase
        .from('prompts')
        .select('room_id')
        .eq('id', promptId)
        .single()

      if (promptErr || !promptRow) return false

      const { data: playerRow } = await supabase
        .from('players')
        .select('id')
        .eq('room_id', promptRow.room_id)
        .eq('user_id', reporterId)
        .maybeSingle()

      if (!playerRow) return false

      const { count, error } = await supabase
        .from('content_reports')
        .select('id', { count: 'exact', head: true })
        .eq('prompt_id', promptId)
        .eq('reporter_id', playerRow.id)

      if (error) throw error
      return (count || 0) > 0
    } catch (error) {
      console.error('Error checking report existence:', error)
      return false
    }
  }

  private mapPlayerFromDB(data: any): Player {
    return {
      id: data.id,
      name: data.name,
      avatar: data.avatar,
      isHost: data.is_host,
      isReady: data.is_ready,
      score: data.score,
    }
  }

  private mapPromptFromDB(data: any): Prompt {
    return {
      id: data.id,
      playerId: data.player_id,
      roomId: data.room_id,
      roundNumber: data.round_number,
      text: data.text,
      imageUrl: data.image_url,
      imageGenerationStatus: data.image_generation_status,
      isLocked: data.is_locked,
      lockedAt: data.locked_at,
      createdAt: data.created_at,
    }
  }

  private mapVoteFromDB(data: any): Vote {
    return {
      id: data.id,
      voterId: data.voter_id,
      promptId: data.prompt_id,
      roomId: data.room_id,
      roundNumber: data.round_number,
      score: data.score,
      createdAt: data.created_at,
    }
  }

  private mapRoundFromDB(data: any): GameRound {
    return {
      id: data.id,
      roomId: data.room_id,
      roundNumber: data.round_number,
      word: data.word,
      status: data.status,
      startTime: data.start_time || data.created_at,
      endTime: data.end_time,
      prompts: [], // Will be populated separately
      votes: [], // Will be populated separately
    }
  }

  // Server-side time calculation functions
  async getRemainingTime(roomId: string): Promise<{ timeLeft: number; isExpired: boolean }> {
    try {
      const { data: room, error } = await supabase
        .from('rooms')
        .select('status, prompt_time_limit, voting_time_limit, round_started_at, voting_started_at')
        .eq('id', roomId)
        .single()

      if (error) throw error

      let startTime: string | null = null
      let timeLimit: number = 0

      if (room.status === 'prompt_writing' && room.round_started_at) {
        startTime = room.round_started_at
        timeLimit = room.prompt_time_limit
      } else if (room.status === 'voting' && room.voting_started_at) {
        startTime = room.voting_started_at
        timeLimit = room.voting_time_limit
      }

      if (!startTime) {
        return { timeLeft: timeLimit, isExpired: false }
      }

      const startTimestamp = new Date(startTime).getTime()
      const now = Date.now()
      const elapsed = Math.floor((now - startTimestamp) / 1000)
      const timeLeft = Math.max(0, timeLimit - elapsed)
      const isExpired = timeLeft <= 0

      return { timeLeft, isExpired }
    } catch (error) {
      console.error('Error calculating remaining time:', error)
      return { timeLeft: 0, isExpired: true }
    }
  }

  // Set round start time (called when game starts or round advances)
  async setRoundStartTime(roomId: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('rooms')
        .update({ round_started_at: new Date().toISOString() })
        .eq('id', roomId)

      if (error) throw error
    } catch (error) {
      console.error('Error setting round start time:', error)
      throw error
    }
  }

  // Set voting start time (called when voting starts)
  async setVotingStartTime(roomId: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('rooms')
        .update({ voting_started_at: new Date().toISOString() })
        .eq('id', roomId)

      if (error) throw error
    } catch (error) {
      console.error('Error setting voting start time:', error)
      throw error
    }
  }

  // Manually check and fix room status (for debugging)
  async checkAndFixRoomStatus(roomId: string): Promise<void> {
    try {
      const { error } = await supabase.rpc('check_and_fix_room_status', {
        p_room_id: roomId,
      })

      if (error) throw error
    } catch (error) {
      console.error('Error checking and fixing room status:', error)
      throw error
    }
  }

  // Kick player from room (only host can kick)
  async kickPlayer(roomId: string, playerIdToKick: string, kickedByHostId: string): Promise<void> {
    try {
      // Ensure the kicker is the host
      const { data: roomData, error: roomError } = await supabase
        .from('rooms')
        .select('host_id')
        .eq('id', roomId)
        .single()

      if (roomError) throw roomError
      if (!roomData) throw new Error('Oda bulunamadı')
      // Map kicker player id -> user_id to compare with rooms.host_id
      const { data: kickerRow, error: kickerError } = await supabase
        .from('players')
        .select('user_id, room_id')
        .eq('id', kickedByHostId)
        .single()

      if (kickerError || !kickerRow || kickerRow.room_id !== roomId) {
        throw new Error('Oda veya kullanıcı doğrulanamadı')
      }
      if (roomData.host_id !== kickerRow.user_id) {
        throw new Error('Sadece lobi sahibi kullanıcı atabilir')
      }

      // Check if player exists in room
      const { data: playerData, error: playerError } = await supabase
        .from('players')
        .select('id, is_host')
        .eq('id', playerIdToKick)
        .eq('room_id', roomId)
        .single()

      if (playerError) throw new Error('Oyuncu odada bulunamadı')
      if (playerData.is_host) {
        throw new Error('Lobi sahibi kendini atamaz')
      }

      // Delete player from room
      const { error: deleteError } = await supabase
        .from('players')
        .delete()
        .eq('id', playerIdToKick)
        .eq('room_id', roomId)

      if (deleteError) throw deleteError
    } catch (error) {
      console.error('Error kicking player:', error)
      throw error
    }
  }

  // User Word operations for user_word_to_prompt mode
  async submitUserWord(
    roomId: string,
    playerId: string,
    roundNumber: number,
    wordOrPhrase: string
  ): Promise<UserWord> {
    try {
      const { data, error } = await supabase
        .from('user_words')
        .insert({
          room_id: roomId,
          player_id: playerId,
          round_number: roundNumber,
          word_or_phrase: wordOrPhrase.trim(),
        })
        .select()
        .single()

      if (error) throw error

      return {
        id: data.id,
        roomId: data.room_id,
        playerId: data.player_id,
        roundNumber: data.round_number,
        wordOrPhrase: data.word_or_phrase,
        createdAt: data.created_at,
      }
    } catch (error) {
      console.error('Error submitting user word:', error)
      throw error
    }
  }

  async getUserWords(roomId: string, roundNumber: number): Promise<UserWord[]> {
    try {
      const { data, error } = await supabase
        .from('user_words')
        .select(`
          *,
          players(name)
        `)
        .eq('room_id', roomId)
        .eq('round_number', roundNumber)
        .order('created_at', { ascending: true })

      if (error) throw error

      return data.map(item => ({
        id: item.id,
        roomId: item.room_id,
        playerId: item.player_id,
        roundNumber: item.round_number,
        wordOrPhrase: item.word_or_phrase,
        createdAt: item.created_at,
      }))
    } catch (error) {
      console.error('Error getting user words:', error)
      throw error
    }
  }

  async getUserWordByPlayer(roomId: string, playerId: string, roundNumber: number): Promise<UserWord | null> {
    try {
      const { data, error } = await supabase
        .from('user_words')
        .select()
        .eq('room_id', roomId)
        .eq('player_id', playerId)
        .eq('round_number', roundNumber)
        .single()

      if (error) {
        if (error.code === 'PGRST116') return null // No rows returned
        throw error
      }

      return {
        id: data.id,
        roomId: data.room_id,
        playerId: data.player_id,
        roundNumber: data.round_number,
        wordOrPhrase: data.word_or_phrase,
        createdAt: data.created_at,
      }
    } catch (error) {
      console.error('Error getting user word by player:', error)
      throw error
    }
  }

  // Override startGame for user_word_to_prompt mode
  async startGameWithUserWords(roomId: string): Promise<void> {
    try {
      // Get room settings and player count
      const { data: roomData, error: roomReadError } = await supabase
        .from('rooms')
        .select(`
          max_rounds, 
          game_mode,
          players!inner(count)
        `)
        .eq('id', roomId)
        .single()

      if (roomReadError) throw roomReadError

      // Get actual player count
      const { data: players, error: playersError } = await supabase
        .from('players')
        .select('id')
        .eq('room_id', roomId)

      if (playersError) throw playersError

      const playerCount = players.length
      const maxRounds = roomData.game_mode === 'user_word_to_prompt' ? playerCount : (roomData.max_rounds || 3)

      if (roomData.game_mode === 'user_word_to_prompt') {
        // For user_word_to_prompt mode, set max_rounds to player count and update status
        const { error: roomError } = await supabase
          .from('rooms')
          .update({
            status: 'waiting',
            round_number: 1,
            max_rounds: maxRounds, // Set to player count
          })
          .eq('id', roomId)

        if (roomError) throw roomError
      } else {
        // Original logic for word_to_prompt mode
        await this.startGame(roomId)
      }
    } catch (error) {
      console.error('Error starting game with user words:', error)
      throw error
    }
  }

  // Start prompt writing phase with user words
  async startPromptWritingWithUserWords(roomId: string, roundNumber: number): Promise<string> {
    try {
      // Get all user words for this round
      const userWords = await this.getUserWords(roomId, roundNumber)

      if (userWords.length === 0) {
        throw new Error('Henüz hiçbir oyuncu kelime göndermedi')
      }

      // Use ALL submitted words for consecutive rounds starting at current roundNumber
      const wordsToUse = userWords.map(w => w.wordOrPhrase)

      // Create/ensure game_rounds for each word in order
      const roundsToInsert = wordsToUse.map((w, idx) => ({
        room_id: roomId,
        round_number: roundNumber + idx,
        word: w,
        status: 'prompt_writing',
      }))

      const { error: roundError } = await supabase
        .from('game_rounds')
        .upsert(roundsToInsert, { onConflict: 'room_id,round_number' })

      if (roundError) throw roundError

      // Start with the first word now
      const firstWord = wordsToUse[0]
      const { error: roomError } = await supabase
        .from('rooms')
        .update({
          status: 'prompt_writing',
          current_word: firstWord,
          round_number: roundNumber,
          round_started_at: new Date().toISOString()
        })
        .eq('id', roomId)

      if (roomError) throw roomError

      return firstWord
    } catch (error) {
      console.error('Error starting prompt writing with user words:', error)
      throw error
    }
  }
}

export const databaseService = new DatabaseService()
export default databaseService
